import React, { useMemo } from 'react';
import { useElectronEnvironment, useFeatureGate as useFeatureGateHook } from '../hooks/useElectronEnvironment';
import { Box, Tooltip, IconButton, Chip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * FeatureGate - Component that conditionally renders based on feature availability
 * 
 * @param {string|string[]} feature - Feature name(s) to check
 * @param {ReactNode} children - Content to render if feature is available
 * @param {ReactNode} fallback - Content to render if feature is not available
 * @param {boolean} requireAll - If multiple features, require all (true) or any (false)
 * @param {boolean} silent - Don't show any UI for unavailable features
 * @param {Function} onFeatureUnavailable - Callback when feature is unavailable
 */
const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  requireAll = true,
  silent = false,
  onFeatureUnavailable,
  ...props 
}) => {
  const { canUseFeature, hasAllFeatures, hasAnyFeature, getFeatureDescription } = useElectronEnvironment();
  
  // Check feature availability
  const isAvailable = useMemo(() => {
    if (Array.isArray(feature)) {
      return requireAll ? hasAllFeatures(...feature) : hasAnyFeature(...feature);
    }
    return canUseFeature(feature);
  }, [feature, canUseFeature, hasAllFeatures, hasAnyFeature, requireAll]);
  
  // Call callback if feature unavailable
  React.useEffect(() => {
    if (!isAvailable && onFeatureUnavailable) {
      onFeatureUnavailable(feature);
    }
  }, [isAvailable, feature, onFeatureUnavailable]);
  
  // If feature is available, render children
  if (isAvailable) {
    return <>{children}</>;
  }
  
  // If silent mode, return fallback without any UI
  if (silent) {
    return fallback;
  }
  
  // If custom fallback provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }
  
  // Default: return null (no UI)
  return null;
};

/**
 * FeatureStatus - Shows the status of a feature (available/unavailable)
 */
export const FeatureStatus = ({ 
  feature, 
  showDescription = true,
  variant = 'chip', // 'chip', 'icon', 'text'
  size = 'small' 
}) => {
  const { canUseFeature, getFeatureDescription } = useElectronEnvironment();
  const isAvailable = canUseFeature(feature);
  const description = getFeatureDescription(feature);
  
  if (variant === 'chip') {
    return (
      <Chip
        size={size}
        icon={isAvailable ? <CheckCircleOutlineIcon /> : <LockOutlinedIcon />}
        label={showDescription ? description : feature}
        color={isAvailable ? 'success' : 'default'}
        variant={isAvailable ? 'filled' : 'outlined'}
      />
    );
  }
  
  if (variant === 'icon') {
    return (
      <Tooltip title={`${description}: ${isAvailable ? 'Available' : 'Requires Desktop App'}`}>
        <IconButton size={size} color={isAvailable ? 'success' : 'default'}>
          {isAvailable ? <CheckCircleOutlineIcon /> : <LockOutlinedIcon />}
        </IconButton>
      </Tooltip>
    );
  }
  
  // Text variant
  return (
    <Typography 
      variant="body2" 
      color={isAvailable ? 'success.main' : 'text.secondary'}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {isAvailable ? <CheckCircleOutlineIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" />}
      {showDescription ? description : feature}
    </Typography>
  );
};

/**
 * FeatureList - Shows a list of features and their availability
 */
export const FeatureList = ({ features, title = 'Feature Availability' }) => {
  const { canUseFeature, getFeatureDescription } = useElectronEnvironment();
  
  const featureStatuses = useMemo(() => {
    const allFeatures = features || [
      'fileOperations',
      'directorySelection',
      'sqliteDatabase',
      'comfyUIIntegration',
      'nativeAuth',
      'localFileUpload',
      'imageMetadataEdit',
      'shellOperations'
    ];
    
    return allFeatures.map(feature => ({
      name: feature,
      description: getFeatureDescription(feature),
      available: canUseFeature(feature)
    }));
  }, [features, canUseFeature, getFeatureDescription]);
  
  return (
    <Box>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <Box display="flex" flexDirection="column" gap={1}>
        {featureStatuses.map(({ name, description, available }) => (
          <Box 
            key={name} 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            p={1}
            sx={{
              borderRadius: 1,
              backgroundColor: available ? 'success.light' : 'action.hover',
              opacity: available ? 1 : 0.7
            }}
          >
            <Typography variant="body2">
              {description}
            </Typography>
            {available ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : (
              <LockOutlinedIcon color="action" fontSize="small" />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * ConditionalFeature - Wrapper that modifies behavior based on feature availability
 */
export const ConditionalFeature = ({ 
  feature, 
  children, 
  disabledProps = {},
  enabledProps = {} 
}) => {
  const { canUseFeature } = useElectronEnvironment();
  const isAvailable = canUseFeature(feature);
  
  // Clone children with appropriate props
  return React.cloneElement(children, {
    ...children.props,
    ...(isAvailable ? enabledProps : disabledProps),
    disabled: !isAvailable || children.props.disabled,
    'data-feature-available': isAvailable
  });
};

/**
 * FeatureTooltip - Shows a tooltip when hovering over disabled features
 */
export const FeatureTooltip = ({ 
  feature, 
  children,
  placement = 'top' 
}) => {
  const { canUseFeature, getFeatureDescription } = useElectronEnvironment();
  const isAvailable = canUseFeature(feature);
  const description = getFeatureDescription(feature);
  
  if (isAvailable) {
    return children;
  }
  
  return (
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">
            Desktop App Required
          </Typography>
          <Typography variant="caption">
            {description} is only available in the desktop application.
          </Typography>
        </Box>
      }
      placement={placement}
    >
      <span>{children}</span>
    </Tooltip>
  );
};

/**
 * Hook for using feature gate in functional components
 */
export const useFeatureGate = (feature, options = {}) => {
  const hook = useFeatureGateHook(feature, options.fallback);
  
  return {
    ...hook,
    FeatureGateWrapper: ({ children }) => (
      <FeatureGate 
        feature={feature} 
        {...options}
      >
        {children}
      </FeatureGate>
    )
  };
};

export default FeatureGate;